/**
 * AutomationSettingsModal — настройки автоматизации для админа.
 * Открывается из TasksView (кнопка ⚙ Автоматизация в шапке).
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/prototype/Icon";
import {
  fetchVisitAutomationConfig, patchVisitAutomationConfig, triggerVisitAutomation,
  type AutomationVisitConfig,
} from "@/api/automation";
import { fetchActiveUsers, userAvatarUrl } from "@/api/dashboard";

interface Props {
  onClose: () => void;
  /** Только superuser имеет право менять — иначе read-only. */
  canEdit: boolean;
}

export function AutomationSettingsModal({ onClose, canEdit }: Props) {
  const qc = useQueryClient();
  const cfgQ = useQuery({
    queryKey: ["automation/visit"],
    queryFn: fetchVisitAutomationConfig,
    staleTime: 5_000,
  });
  const usersQ = useQuery({
    queryKey: ["users/active"],
    queryFn: fetchActiveUsers,
    staleTime: 5 * 60_000,
  });

  const [local, setLocal] = useState<AutomationVisitConfig | null>(null);
  useEffect(() => { if (cfgQ.data && !local) setLocal(cfgQ.data); }, [cfgQ.data, local]);
  const cfg = local ?? cfgQ.data ?? null;

  const [userSearch, setUserSearch] = useState("");
  const filteredUsers = (usersQ.data ?? []).filter(u => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return true;
    return (u.display_name ?? "").toLowerCase().includes(q);
  });

  const saveMut = useMutation({
    mutationFn: () => {
      if (!cfg) throw new Error("no config");
      return patchVisitAutomationConfig({
        create_folder: cfg.create_folder,
        create_task: cfg.create_task,
        assignee_mode: cfg.assignee_mode,
        fixed_assignee_user_id: cfg.fixed_assignee_user_id,
      });
    },
    onSuccess: (saved) => {
      setLocal(saved);
      qc.invalidateQueries({ queryKey: ["automation/visit"] });
    },
    onError: (e: Error) => alert(`Ошибка: ${e.message}`),
  });

  const triggerMut = useMutation({
    mutationFn: triggerVisitAutomation,
    onSuccess: (stats) => {
      alert(`Обработано: ${stats.processed}\nПапки: ${stats.folders_created}\nЗадачи: ${stats.tasks_created}\nПропущено: ${stats.skipped}`);
    },
    onError: (e: Error) => alert(`Ошибка: ${e.message}`),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="task-modal-bg" onClick={onClose}>
      <div className="task-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(520px, 100%)" }}>
        <div className="task-wiz-header">
          <div className="task-wiz-title-row">
            <div className="task-wiz-title"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: -2 }}><path d="M4 7h8M16 7h4M4 17h4M12 17h8M14 4v6M8 14v6"/></svg>Автоматизация: «Записаны на выезд»</div>
            <button type="button" className="task-wiz-close" onClick={onClose} aria-label="Закрыть">
              <Icon name="x" size={18}/>
            </button>
          </div>
        </div>

        <div className="task-modal-body">
          {cfgQ.isLoading && <div className="task-wiz-hint">Загрузка…</div>}
          {!canEdit && (
            <div className="task-wiz-hint" style={{ color: "#c62828" }}>
              Только администратор может менять настройки.
            </div>
          )}
          {cfg && (
            <>
              <div className="auto-row">
                <label className="auto-toggle">
                  <input
                    type="checkbox"
                    disabled={!canEdit}
                    checked={cfg.create_folder}
                    onChange={(e) => setLocal({ ...cfg, create_folder: e.target.checked })}
                  />
                  <div>
                    <div className="auto-toggle-label">Создавать папку на диске</div>
                    <div className="auto-toggle-sub">
                      В «Диск (Продажи)» появится папка с названием сделки
                    </div>
                  </div>
                </label>
              </div>

              <div className="auto-row">
                <label className="auto-toggle">
                  <input
                    type="checkbox"
                    disabled={!canEdit}
                    checked={cfg.create_task}
                    onChange={(e) => setLocal({ ...cfg, create_task: e.target.checked })}
                  />
                  <div>
                    <div className="auto-toggle-label">Создавать задачу</div>
                    <div className="auto-toggle-sub">
                      Задача «Подготовить спецификацию, ТЗ и КП» с дедлайном на следующий рабочий день, 18:00
                    </div>
                  </div>
                </label>
              </div>

              {cfg.create_task && (
                <div className="auto-row" style={{ paddingLeft: 28 }}>
                  <div className="task-field-label" style={{ marginBottom: 8 }}>На кого ставить задачу</div>
                  <div className="task-chip-row" style={{ marginBottom: 10 }}>
                    <button
                      type="button"
                      disabled={!canEdit}
                      className={`task-chip${cfg.assignee_mode === "responsible" ? " is-active" : ""}`}
                      onClick={() => setLocal({ ...cfg, assignee_mode: "responsible" })}
                    >
                      <span className="task-chip-label">Ответственный менеджер</span>
                    </button>
                    <button
                      type="button"
                      disabled={!canEdit}
                      className={`task-chip${cfg.assignee_mode === "fixed" ? " is-active" : ""}`}
                      onClick={() => setLocal({ ...cfg, assignee_mode: "fixed" })}
                    >
                      <span className="task-chip-label">Конкретный человек</span>
                    </button>
                  </div>
                  {cfg.assignee_mode === "responsible" && (
                    <div className="task-wiz-hint">
                      Менеджер из AmoCRM (responsible_user) маппится по имени к пользователю мессенджера.
                    </div>
                  )}
                  {cfg.assignee_mode === "fixed" && (
                    <>
                      <input
                        type="search"
                        placeholder="Поиск по имени…"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="task-user-search"
                      />
                      <div className="task-user-list task-user-list-tall">
                        {filteredUsers.map(u => {
                          const checked = cfg.fixed_assignee_user_id === u.id;
                          const avaUrl = userAvatarUrl(u.id, u.avatar_filename ?? null);
                          return (
                            <button
                              key={u.id}
                              type="button"
                              disabled={!canEdit}
                              className={`task-user-row${checked ? " is-selected" : ""}`}
                              onClick={() => setLocal({ ...cfg, fixed_assignee_user_id: u.id })}
                            >
                              <input type="radio" checked={checked} readOnly/>
                              {avaUrl ? (
                                <img src={avaUrl} alt="" className="task-user-avatar" loading="lazy"/>
                              ) : (
                                <span className="task-user-avatar task-user-avatar-fallback">
                                  {(u.display_name ?? "?")[0]}
                                </span>
                              )}
                              <div className="task-user-info">
                                <div className="task-user-name">{u.display_name ?? "(без имени)"}</div>
                                {u.position && <div className="task-user-pos">{u.position}</div>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              {canEdit && (
                <div className="auto-row">
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    disabled={triggerMut.isPending}
                    onClick={() => triggerMut.mutate()}
                  >
                    🔄 Запустить обработку сейчас (тест)
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="task-modal-actions">
          <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>Закрыть</button>
          {canEdit && (
            <button
              type="button"
              className="btn btn-sm"
              disabled={saveMut.isPending || !cfg}
              onClick={() => saveMut.mutate()}
            >
              {saveMut.isPending ? "Сохранение…" : "Сохранить"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
